import type { Assessment } from '@seen/core/models/assessments/assessment'
import { resolveId } from '@seen/core/references'
import { Button } from '@seen/ui'
import { IconChevronRight, IconDots } from '@tabler/icons-react'
import type { JSX } from 'react'
import { LinkCell } from '#src/components/core/data-table/LinkCell.tsx'
import { DropdownMenu } from '#src/components/core/dropdown-menu/DropdownMenu.tsx'
import { DropdownMenuContent } from '#src/components/core/dropdown-menu/DropdownMenuContent.tsx'
import { DropdownMenuDeleteItem } from '#src/components/core/dropdown-menu/DropdownMenuDeleteItem.tsx'
import { DropdownMenuTrigger } from '#src/components/core/dropdown-menu/DropdownMenuTrigger.tsx'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { ParticipantName } from '#src/components/ParticipantName.tsx'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { extractTitleFromHTML } from '#src/utils/html.ts'

export function AssessmentTitleCell({
  assessment,
}: {
  assessment: Assessment
}): JSX.Element {
  return (
    <LinkCell
      to={`/Patient/${resolveId(assessment.subject)}/assessments/${assessment.id}`}
    >
      {assessment.title.trim() ? assessment.title.trim() : 'Untitled'}
    </LinkCell>
  )
}

export function AssessmentParticipantCell({
  assessment,
}: {
  assessment: Assessment
}): JSX.Element {
  return (
    <LinkCell
      to={`/Patient/${resolveId(assessment.subject)}/assessments/${assessment.id}`}
    >
      <ParticipantName participant={assessment.subject} />
    </LinkCell>
  )
}

export function AssessmentLastUpdatedCell({
  assessment,
}: {
  assessment: Assessment
}): JSX.Element {
  return (
    <LinkCell
      to={`/Patient/${resolveId(assessment.subject)}/assessments/${assessment.id}`}
    >
      <HoverDate date={assessment.lastUpdated} />
    </LinkCell>
  )
}

export function AssessmentActionsCell({
  assessment,
}: {
  assessment: Assessment
}): JSX.Element {
  const mutate = usePatchMutation<Assessment>().mutate
  return (
    <LinkCell
      to={`/Patient/${resolveId(assessment.subject)}/assessments/${assessment.id}`}
      className="flex items-center justify-end gap-1"
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="-my-3">
            <IconDots className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuDeleteItem
            deleteConfirmationProps={{
              title: `Delete "${extractTitleFromHTML(assessment.title)}"?`,
              description:
                'Deleted assessments can still be recovered by contacting our team.',
              onConfirm: () => {
                mutate({
                  instance: assessment,
                  mutateFn: (t) => {
                    t.status = 'entered-in-error'
                  },
                })
              },
            }}
          />
        </DropdownMenuContent>
      </DropdownMenu>
      <Button variant="ghost" className="-my-3 -mr-2" size="icon">
        <IconChevronRight className="size-4" />
      </Button>
    </LinkCell>
  )
}
